import { Component, OnDestroy, OnInit, Renderer2, ViewChild, DestroyRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { MenuService } from './app.menu.service';
import { AppTopbarComponent } from './components/app-topbar/app.topbar.component';
import { AppSidebarComponent } from './components/app-sidebar/app.sidebar.component';
import { LayoutService } from './service/app.layout.service';
import { selectHasValidSubscription, selectSubscriptionLoading } from '../shared/state/subscription/subscription.selectors';
import { loadSubscriptionStatus } from '../shared/state/subscription/subscription.actions';
import { selectProfile } from '../shared/state/profile/profile.selectors';
import { ProfileInfoDto } from '../shared/interfaces/profile/profile';
import { ToastServiceService } from '../shared/services/toast-service.service';

@Component({
  selector: 'app-layout',
  templateUrl: './app.layout.component.html',
  styleUrls: ['./app.layout.component.scss'],
})
export class AppLayoutComponent implements OnInit, OnDestroy {
  overlayMenuOpenSubscription: Subscription;

  menuOutsideClickListener: any;

  menuScrollListener: any;

  hasValidSubscription = false;
  subscriptionLoading = true;
  isCompanyOwner = false;
  isSubscriptionPage = false;

  @ViewChild(AppSidebarComponent) appSidebar!: AppSidebarComponent;

  @ViewChild(AppTopbarComponent) appTopbar!: AppTopbarComponent;

  constructor(
    private menuService: MenuService,
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private store: Store,
    private destroyRef: DestroyRef,
    private toastService: ToastServiceService
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          const isOutsideClicked = !(
            this.appSidebar.el.nativeElement.isSameNode(event.target) ||
            this.appSidebar.el.nativeElement.contains(event.target) ||
            this.appTopbar.menuButton.nativeElement.isSameNode(event.target) ||
            this.appTopbar.menuButton.nativeElement.contains(event.target)
          );
          if (isOutsideClicked) {
            this.hideMenu();
          }
        });
      }

      if (
        (this.layoutService.isHorizontal() || this.layoutService.isSlim() || this.layoutService.isSlimPlus()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(this.appSidebar.menuContainer.nativeElement, 'scroll', (event) => {
          if (this.layoutService.isDesktop()) {
            this.hideMenu();
          }
        });
      }

      if (this.layoutService.state.staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
      // Update subscription page flag on navigation
      this.isSubscriptionPage = this.router.url.includes('/subscription');
    });
  }

  ngOnInit(): void {
    // Load subscription status if not already loaded
    this.store.dispatch(loadSubscriptionStatus());

    // Check if current route is /subscription
    this.isSubscriptionPage = this.router.url.includes('/subscription');

    // Subscribe to profile to check if user is company owner
    this.store
      .select(selectProfile)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((profile: ProfileInfoDto | null) => {
        this.isCompanyOwner = profile?.groups?.includes('COMPANY_OWNER') || false;
      });

    // Subscribe to subscription loading state
    this.store
      .select(selectSubscriptionLoading)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading: boolean) => {
        this.subscriptionLoading = loading;
      });

    // Subscribe to valid subscription state
    this.store
      .select(selectHasValidSubscription)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasValid: boolean) => {
        this.hasValidSubscription = hasValid;
      });
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.menuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  get containerClass() {
    return {
      'layout-light': this.layoutService.config().colorScheme === 'light',
      'layout-dim': this.layoutService.config().colorScheme === 'dim',
      'layout-dark': this.layoutService.config().colorScheme === 'dark',
      'layout-colorscheme-menu': this.layoutService.config().menuTheme === 'colorScheme',
      'layout-primarycolor-menu': this.layoutService.config().menuTheme === 'primaryColor',
      'layout-transparent-menu': this.layoutService.config().menuTheme === 'transparent',
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-slim': this.layoutService.config().menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
      'layout-horizontal': this.layoutService.config().menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config().menuMode === 'reveal',
      'layout-drawer': this.layoutService.config().menuMode === 'drawer',
      'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'p-input-filled': this.layoutService.config().inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config().ripple,
      'layout-sidebar-active': this.layoutService.state.sidebarActive,
      'layout-sidebar-anchored': this.layoutService.state.anchored,
    };
  }

  /**
   * Determines if the router-outlet should be shown.
   * Shows when subscription is valid, loading, or company owner is on subscription page.
   */
  get shouldShowRouterOutlet(): boolean {
    return this.subscriptionLoading || this.hasValidSubscription || (this.isCompanyOwner && this.isSubscriptionPage);
  }

  /**
   * Determines if the glass effect overlay should be shown.
   * Shows when subscription is invalid and not a company owner on subscription page.
   */
  get shouldShowGlassOverlay(): boolean {
    return !this.subscriptionLoading && !this.hasValidSubscription && !(this.isCompanyOwner && this.isSubscriptionPage);
  }

  /**
   * Determines if the subscription background should be applied.
   * Applied when showing glass overlay.
   */
  get shouldShowSubscriptionBackground(): boolean {
    return this.shouldShowGlassOverlay;
  }

  onUpgradeClick(): void {
    // Check if user is company owner synchronously from store
    this.store
      .select(selectProfile)
      .pipe(take(1))
      .subscribe((profile: ProfileInfoDto | null) => {
        const isCompanyOwner = profile?.groups?.includes('COMPANY_OWNER') || false;
        console.log('profile', profile);
        console.log('isCompanyOwner', isCompanyOwner);

        if (!isCompanyOwner) {
          // Show error toast for non-company owners
          this.toastService.showFail('Σφάλμα', 'Μόνο ο ιδιοκτήτης της εταιρείας μπορεί να αναβαθμίσει τη συνδρομή');
          return;
        }

        // Company owner: navigate to subscription page
        const currentUrl = this.router.url;
        this.router.navigate(['/subscription'], { queryParams: { returnUrl: currentUrl } });
      });
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
